import axios from '@/axios.js'

/**
 * Émission d'un bon fiscal sur la casa de marcat, depuis une fiche facture.
 *
 * Frère du mixin `chitanta.js` : à l'encaissement en espèces, l'opérateur fait
 * l'un OU l'autre — une chitanță (reçu papier produit par l'ERP) ou un bon
 * fiscal (produit par la caisse). Les deux ne s'additionnent pas.
 *
 * Contrairement à la chitanță, ce mixin ne fabrique AUCUN document : il met une
 * demande en file côté serveur. Le bon est construit, vérifié et gelé par
 * l'API, puis déposé dans \Bonuri par l'agent CasaMarcat sur le poste du
 * pilote. Un navigateur ne peut pas écrire de fichier, et le contenu d'un
 * document fiscal irréversible ne doit pas dépendre d'un client manipulable.
 *
 * LE BOUTON DIT CE QUI S'EST PASSÉ. Un second envoi est impossible (index
 * unique en base), mais un bouton qui reste « Fiscal receipt » après
 * impression invite à recliquer, et renvoie une erreur 409 incompréhensible.
 * Le bouton affiche donc l'état réel du bon de CETTE facture, lu sur le
 * serveur — pas un état local qui mentirait après un rechargement ou dans un
 * second onglet.
 */

// Ce qui bloque une nouvelle émission n'est PAS redécidé ici : le serveur le
// dit par `blocks_reissue`, calculé sur la même liste que l'index unique en
// base. Une copie locale finirait par diverger, et le bouton proposerait alors
// un envoi que la base refuse, ou l'inverse.
const IN_PROGRESS = ['queued', 'leased', 'writing']

// Relecture après une mise en file, pour que le bouton passe de « en cours » à
// « imprimé » sous les yeux. 60 s couvrent largement le bail de l'agent ; au-delà
// on arrête de solliciter le serveur, l'état se relira au prochain affichage.
const POLL_EVERY_MS = 3000
const POLL_FOR_MS = 60000

export default {
  data() {
    return {
      casamarcatBusy: false,
      // Le job qui fait foi pour cette facture (null = aucun bon demandé).
      fiscalJob: null,
      fiscalStatusLoaded: false,
      fiscalPollTimer: null,
      fiscalPollUntil: 0,
    }
  },

  computed: {
    /**
     * Tout ce qu'affiche le bouton, dérivé de l'état réel du bon.
     * `action` est la méthode à appeler au clic ; null = bouton inactif.
     */
    fiscalButton() {
      const job = this.fiscalJob

      if (this.casamarcatBusy) {
        return { label: 'Sending…', color: 'warning', icon: 'icon-loader', disabled: true, action: null }
      }

      if (!job) {
        return { label: 'Fiscal receipt', color: 'danger', icon: 'icon-printer', disabled: false, action: 'ConfirmFiscalReceipt' }
      }

      if (IN_PROGRESS.includes(job.status)) {
        return { label: 'Printing fiscal receipt…', color: 'warning', icon: 'icon-loader', disabled: true, action: null }
      }

      if (job.status === 'printed' || job.status === 'resolved_printed') {
        const n = job.receipt_no ? ` n° ${job.receipt_no}` : ''
        const when = this.fiscalDate(job.printed_at || job.resolved_at || job.updated_at)
        return {
          label: `✓ Fiscal receipt${n} — printed ${when}`,
          color: 'success',
          icon: 'icon-check-circle',
          // Jamais cliquable : un bon imprimé ne se réimprime pas. Une
          // correction passe par un bon de storno, pas par ce bouton.
          disabled: true,
          action: null,
        }
      }

      if (job.status === 'unknown') {
        return {
          label: '⚠ Receipt to verify — may have printed',
          color: 'warning',
          icon: 'icon-alert-triangle',
          // Cliquable, mais vers l'arbitrage, JAMAIS vers une nouvelle
          // émission : le papier est peut-être sorti, et réémettre encaisserait
          // le client deux fois.
          disabled: false,
          action: 'OpenFiscalReview',
        }
      }

      // failed / rejected / resolved_not_printed : on sait que rien n'est
      // sorti. La ré-émission est permise, mais on dit qu'il y a eu un échec.
      return {
        label: 'Fiscal receipt — previous attempt failed',
        color: 'danger',
        icon: 'icon-printer',
        disabled: false,
        action: 'ConfirmFiscalReceipt',
      }
    },
  },

  mounted() {
    // La facture est chargée de façon asynchrone : on attend son id.
    this.$watch(
      () => this.user_data && this.user_data.id,
      (id) => { if (id) this.loadFiscalStatus() },
      { immediate: true }
    )
  },

  beforeDestroy() {
    this.stopFiscalPoll()
  },

  methods: {
    /** Clic sur le bouton : route vers l'action que l'état autorise. */
    onFiscalButton() {
      const action = this.fiscalButton.action
      if (action && typeof this[action] === 'function') this[action]()
    },

    /**
     * Lit sur le serveur l'état du bon de cette facture.
     *
     * Le job qui fait foi est le premier BLOQUANT s'il en existe un (c'est lui
     * qui interdit une nouvelle émission), sinon le plus récent. Une facture
     * peut avoir plusieurs jobs : tentatives échouées puis réussie.
     */
    loadFiscalStatus() {
      if (!this.user_data || !this.user_data.id) return Promise.resolve()

      return axios.get(`/fiscal/jobs?idoperation=${this.user_data.id}`)
        .then((res) => {
          const jobs = (Array.isArray(res.data) ? res.data : [])
            .filter((j) => j.kind !== 'service')
          const blocking = jobs.find((j) => j.blocks_reissue)
          this.fiscalJob = blocking || jobs[0] || null
          this.fiscalStatusLoaded = true

          // Plus rien en cours : inutile de continuer à interroger.
          if (!this.fiscalJob || !IN_PROGRESS.includes(this.fiscalJob.status)) {
            this.stopFiscalPoll()
          }
        })
        .catch(() => {
          // En cas d'échec de lecture, on laisse l'état tel quel plutôt que de
          // l'effacer : afficher « Fiscal receipt » à tort inviterait à
          // recliquer. La base refuserait de toute façon un second bon.
          this.fiscalStatusLoaded = true
        })
    },

    startFiscalPoll() {
      this.stopFiscalPoll()
      this.fiscalPollUntil = Date.now() + POLL_FOR_MS
      this.fiscalPollTimer = setInterval(() => {
        if (Date.now() > this.fiscalPollUntil) { this.stopFiscalPoll(); return }
        this.loadFiscalStatus()
      }, POLL_EVERY_MS)
    },

    stopFiscalPoll() {
      if (this.fiscalPollTimer) clearInterval(this.fiscalPollTimer)
      this.fiscalPollTimer = null
    },

    fiscalDate(value) {
      if (!value) return ''
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return ''
      const pad = (n) => String(n).padStart(2, '0')
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    },

    /** Un bon indéterminé s'arbitre sur la page de la file, pas ici. */
    OpenFiscalReview() {
      this.$router.push({ path: '/fiscal-jobs' }).catch(() => {})
    },

    /**
     * Demande l'émission, après confirmation.
     *
     * La confirmation n'est pas une formalité : une fois le fichier déposé, la
     * caisse imprime, et un bon fiscal ne s'annule pas. On annonce donc le
     * montant exact et le caractère définitif avant d'enfiler.
     */
    ConfirmFiscalReceipt() {
      const total = typeof this.docTotalTtcRon === 'number' ? this.docTotalTtcRon : null

      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Issue a fiscal receipt?',
        text:
          (total !== null ? `${total.toFixed(2)} RON ` : '')
          + 'will be printed on the cash register and recorded in its electronic journal '
          + 'and Z report. A fiscal receipt cannot be cancelled.',
        acceptText: 'Issue',
        cancelText: 'Cancel',
        accept: this.EmitFiscalReceipt,
      })
    },

    EmitFiscalReceipt() {
      if (!this.user_data || !this.user_data.id) {
        this.$vs.notify({ color: 'danger', title: 'Error', text: 'Invoice not loaded.' })
        return Promise.resolve()
      }

      this.casamarcatBusy = true

      return axios.post('/fiscal/receipt', { idoperation: this.user_data.id })
        .then((res) => {
          const jobId = res.data && res.data.job_id
          this.$vs.notify({
            color: 'success',
            title: 'Queued for printing',
            text: `Job #${jobId}. The register will print it within seconds.`,
            time: 8000,
          })
          // Les avertissements non bloquants (unité inconnue repliée sur « buc »,
          // par exemple) sont affichés à part : ils ne justifient pas de
          // refuser, mais l'opérateur doit savoir ce qui sortira sur le papier.
          const warnings = (res.data && res.data.warnings) || []
          if (warnings.length) {
            this.$vs.notify({
              color: 'warning',
              title: 'Printed with adjustments',
              text: warnings.join(' · '),
              time: 12000,
            })
          }
          // Le bouton suit l'impression sans rechargement.
          this.startFiscalPoll()
        })
        .catch((err) => {
          const data = (err.response && err.response.data) || {}
          const status = err.response && err.response.status

          // 409 = refus métier attendu (déjà un bon, pas payé en espèces, CNP
          // manquant au-delà de 1 000 RON…). 422 = le bon ne peut pas être
          // construit correctement. Dans les deux cas le message du serveur est
          // explicite et actionnable : on le montre tel quel plutôt que de le
          // remplacer par « une erreur est survenue ».
          this.$vs.notify({
            color: 'danger',
            title: status === 409 ? 'Cannot issue this receipt' : 'Receipt refused',
            text: data.error || err.message,
            time: 14000,
          })
        })
        .finally(() => {
          this.casamarcatBusy = false
          // Relecture dans tous les cas, succès ou refus : un 409 signifie que
          // l'état affiché était périmé (bon émis depuis un autre onglet ou un
          // autre poste). Le bouton se corrige tout seul.
          this.loadFiscalStatus()
        })
    },
  },
}
